<template>
    <div class="relative flex w-full flex-col">
        <div
            v-if="isDefaultMessage"
            class="text-md"
        >
            Are you sure you want to delete this {{ itemType }}?
        </div>
        <div
            v-else
            class="text-md"
        >
            {{ customMessage }}
        </div>
        <div class="container mt-3 divide-y divide-muval-gray-8 rounded-muval-2 bg-muval-gray-5 dark:bg-gray-900">
            <div class="grid grid-cols-5 gap-y-2 p-4 text-sm">
                <span class="font-medium">{{ itemType }}</span>
                <span class="col-span-4">{{ data.id }}</span>
                <template
                    v-for="item in deleteItemSchema"
                    :key="item"
                >
                    <span class="font-medium">{{ item.name }}</span>
                    <!-- Thankyou Stack Overflow: https://stackoverflow.com/questions/6491463/accessing-nested-javascript-objects-and-arrays-by-string-path -->
                    <span class="col-span-4">
                        {{ `${item.value}`.split('.').reduce((p, c) => (p && p[c]) || null, data) }}
                    </span>
                </template>
                <template v-if="data.created_at">
                    <span class="font-medium">Created</span>
                    <time class="col-span-4">
                        {{ $dayjs(data.created_at).format('dddd DD MMM YYYY') }} at
                        {{ $dayjs(data.created_at).format('h:mm A') }}
                    </time>
                </template>
            </div>
        </div>
        <div class="mt-4 flex flex-col justify-end gap-2 md:flex-row">
            <MuvalButton
                cancelBtn
                class="order-2 md:order-1"
                @click="closeModal"
            >
                No, keep this item
            </MuvalButton>
            <MuvalButton
                :loading="loading"
                class="order-1 md:order-2"
                @click="remove"
            >
                {{ customConfirmButtonText ? customConfirmButtonText : 'Yes, delete this ' + itemType }}
            </MuvalButton>
        </div>
    </div>
</template>

<script setup>
import useEmitter from '@plugins/useEmitter';
import MuvalButton from '@buttons/MuvalButton.vue';
import { toast } from 'vue-sonner';

const props = defineProps({
    itemIndex: {
        type: Number,
        required: true,
    },
    itemType: {
        type: [String, null],
        required: false,
        default: null,
    },
    data: {
        type: [Object, null],
        required: false,
        default: null,
    },
    url: {
        type: [String, null],
        required: true,
        default: null,
    },
    deleteItemSchema: {
        type: [Array, null],
        required: false,
        default: null,
    },
    emitKey: {
        type: [String, null],
        required: false,
        default: null,
    },
    scope: {
        type: String,
        required: false,
        default: 'tenant',
    },
    isDefaultMessage: {
        type: Boolean,
        required: false,
        default: true,
    },
    customMessage: {
        type: String,
        required: false,
        default: null,
    },
    customConfirmButtonText: {
        type: String,
        required: false,
        default: null,
    },
    /**
     * Whether this record can be brought back, which decides whether the success toast offers an
     * Undo action.
     *
     * The action PUTs `{url}/restore/{id}`, and that route does not exist for every deletable
     * thing — the non-move services (packing, unpacking, packing materials, car transport, pet
     * transport) have none, so their Undo could only 404 and appear to do nothing at all
     * (CU-86d45pvyd, Jay's review). Defaults to true so every existing caller keeps its Undo.
     */
    restorable: {
        type: Boolean,
        required: false,
        default: true,
    },
});

const axios = computed(() => {
    if (props.scope === 'tenant') {
        return window.tenantAxios;
    } else {
        return window.centralAxios;
    }
});

const emit = defineEmits(['close:modal', 'remove:item']);
const loading = ref(false);
const emitter = useEmitter();

const lastDeleted = ref({
    index: null,
    id: null,
});

async function remove() {
    try {
        loading.value = true;

        const response = await axios.value.delete(`${props.url}/${props.data.id}`);

        loading.value = false;

        if (response) {
            lastDeleted.value.index = props.itemIndex;
            lastDeleted.value.id = props.data.id;

            toast('Item has been deleted.', props.restorable ? { action: undoAction() } : undefined);

            if (props.emitKey) {
                emitter.emit(`remove:item-${props.emitKey}`, props.data.id);
            } else {
                emit('remove:item', props.data.id);
            }
        }

        closeModal();
    } catch (error) {
        loading.value = false;
        console.log('DeleteModal@error', error);

        // Display the error message in the toast
        let errorMessage;
        if (error?.message) {
            errorMessage = error?.message;
        } else if (error?.data?.error) {
            errorMessage = error?.data?.error;
        } else {
            errorMessage = 'Failed to delete item. Please try again.';
        }

        toast.error(errorMessage);
    }
}

/**
 * The Undo action on the success toast.
 *
 * The restore is caught rather than left to reject: the axios interceptor already toasts the
 * server's message, but an unhandled rejection here left the operator with a toast they could not
 * connect to the click, and no statement that the item is still gone.
 */
function undoAction() {
    return {
        label: 'Undo',
        onClick: async () => {
            try {
                const response = await axios.value.put(`${props.url}/restore/${lastDeleted.value.id}`);

                if (response) {
                    emitter.emit(props.emitKey ? `restore-item-${props.emitKey}` : 'restore-item', {
                        index: lastDeleted.value.index,
                        object: response.data.restored,
                    });
                    toast(
                        `${props.itemType.toUpperCase().substring(0, 1) + props.itemType.toLowerCase().substring(1)} restored.`,
                    );
                }
            } catch (error) {
                console.log('DeleteModal@restore', error);
                toast.error(`Could not restore this ${props.itemType ?? 'item'}. It is still deleted.`);
            }
        },
    };
}

function closeModal() {
    emit('close:modal');
}
</script>
